import { useState } from "react";
import { createPortal } from "react-dom";

import {
  formatSpeedMultiplier,
  PLAYBACK_SPEEDS,
  type ScrubDirection,
} from "@/lib/timeline/playback";
import { TimelineTransportScrub } from "./TimelineTransportScrub";
import { TimelineTransportToggles } from "./TimelineTransportToggles";
import { IconRepeat, IconRestart } from "./TimelineTransportIcons";

interface TimelineTransportControlsProps {
  playing: boolean;
  speed: number;
  onPlayPause: () => void;
  onRestart: () => void;
  repeat: boolean;
  onRepeatToggle: () => void;
  onScrub: (direction: ScrubDirection) => void;
  onSkipScene: (direction: -1 | 1) => void;
  onSpeedChange: (speed: number) => void;
  cameraLockEnabled: boolean;
  autoZoomEnabled: boolean;
  onCameraLockToggle: () => void;
  onAutoZoomToggle: () => void;
  /** On a phone, the extra controls live in the sheet this opens. */
  phoneSheet?: { onOpen: () => void; host: HTMLElement | null } | undefined;
}

export function TimelineTransportControls({
  playing,
  speed,
  onPlayPause,
  onRestart,
  repeat,
  onRepeatToggle,
  onScrub,
  onSkipScene,
  onSpeedChange,
  cameraLockEnabled,
  autoZoomEnabled,
  onCameraLockToggle,
  onAutoZoomToggle,
  phoneSheet,
}: TimelineTransportControlsProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const extras = (
      <div className="timeline-transport__extras" data-open={moreOpen}>
      <select
        className="timeline-transport__speed"
        aria-label="Speed"
        title="Speed"
        value={speed}
        onChange={(event) => {
          onSpeedChange(Number(event.target.value));
        }}
      >
        {PLAYBACK_SPEEDS.map((option) => (
          <option key={option} value={option}>
            {formatSpeedMultiplier(option)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="timeline-transport__btn"
        onClick={onRestart}
        aria-label="Restart"
        title="Restart"
      >
        <IconRestart className="timeline-transport__icon" />
      </button>
      <button
        type="button"
        className={
          repeat
            ? "timeline-transport__btn timeline-transport__btn--toggle-on"
            : "timeline-transport__btn"
        }
        onClick={onRepeatToggle}
        aria-pressed={repeat}
        aria-label="Repeat"
        title="Repeat"
      >
        <IconRepeat className="timeline-transport__icon" />
      </button>
      <TimelineTransportToggles
        cameraLockEnabled={cameraLockEnabled}
        autoZoomEnabled={autoZoomEnabled}
        onCameraLockToggle={onCameraLockToggle}
        onAutoZoomToggle={onAutoZoomToggle}
      />
      </div>
  );
  return (
    <div className="timeline-transport__controls">
      <TimelineTransportScrub
        playing={playing}
        onPlayPause={onPlayPause}
        onScrub={onScrub}
        onSkipScene={onSkipScene}
      />
      <button
        type="button"
        className="timeline-transport__btn timeline-transport__more"
        aria-expanded={moreOpen}
        aria-label="More controls"
        onClick={() => (phoneSheet === undefined ? setMoreOpen(!moreOpen) : phoneSheet.onOpen())}
      >
        ⋯
      </button>
      {phoneSheet === undefined
        ? extras
        : phoneSheet.host === null ? null : createPortal(extras, phoneSheet.host)}
    </div>
  );
}
